import { useEffect, useRef, useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ChannelsService from '@/services/channels.service';
import UserService from '@/services/user.service';
import ProfileNotificationsIcon from '@/shared-components/icons/components/user-profile/ProfileNotificationsIcon';
import ToggleSwitch from '@/shared-components/toggles/ToggleSwitch';

import {
  areChannelNotificationsEnabled,
  getFollowNotificationCategories,
  getFollowNotificationProfileType,
  normaliseFollowNotificationPreferences } from './followNotificationPreferences.config';

import './styles/FollowNotificationControl.css';

function isJoinedToProfileChannel(ownedChannels, joinedChannels) {
  const joinedChannelIds = new Set(joinedChannels.map((channel) => String(channel?._id || channel?.id || '')));
  return ownedChannels.some((channel) => joinedChannelIds.has(String(channel?._id || channel?.id || '')));
}

export default function FollowNotificationControl({ hasShare = false, profile }) {
  const { state } = useStateHandler();
  const t = useTranslations('common.profile.top_buttons.follow_notifications');
  const controlRef = useRef(null);
  const profileId = profile?._id || profile?.user_id;
  const profileType = getFollowNotificationProfileType(profile);
  const [open, setOpen] = useState(false);
  const [hasJoinedChannel, setHasJoinedChannel] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [savingCategory, setSavingCategory] = useState(null);

  const allCategories = getFollowNotificationCategories(profile, { hasJoinedChannel: true });
  const categories = getFollowNotificationCategories(profile, { hasJoinedChannel });
  const persistedPreferences = normaliseFollowNotificationPreferences(allCategories, preferences);
  const channelNotificationsEnabled = areChannelNotificationsEnabled(state.user_session);

  useEffect(() => {
    if (!profileId || (profileType !== 'gallery' && profileType !== 'artist')) {
      setHasJoinedChannel(false);
      return undefined;
    }

    let active = true;
    Promise.all([ChannelsService.getByOwner(profileId), ChannelsService.getMemberOf('me')])
      .then(([ownedResult, joinedResult]) => {
        if (!active) return;
        const ownedChannels = Array.isArray(ownedResult?.data?.data) ? ownedResult.data.data : [];
        const joinedChannels = Array.isArray(joinedResult?.data?.data) ? joinedResult.data.data : [];
        setHasJoinedChannel(isJoinedToProfileChannel(ownedChannels, joinedChannels));
      })
      .catch(() => {
        if (active) setHasJoinedChannel(false);
      });

    return () => {
      active = false;
    };
  }, [profileId, profileType]);

  useEffect(() => {
    if (!profileId) return undefined;

    let active = true;
    UserService.getFollowNotificationPreferences(profileId)
      .then(({ response, data }) => {
        if (active && response?.ok) {
          setPreferences(data?.notification_preferences || {});
        }
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [profileId]);

  useEffect(() => {
    function closeOnOutsideInteraction(event) {
      if (controlRef.current && !controlRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function closeOnEscape(event) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', closeOnOutsideInteraction);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideInteraction);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const visiblePreferences = normaliseFollowNotificationPreferences(categories, preferences);

  async function updateCategory(categoryId, enabled) {
    if (!profileId || savingCategory) return;

    const previousPreferences = preferences;
    const nextPreferences = { ...persistedPreferences, [categoryId]: enabled };
    setPreferences(nextPreferences);
    setSavingCategory(categoryId);

    try {
      const { response, data } = await UserService.updateFollowNotificationPreferences(profileId, nextPreferences);
      if (!response?.ok) throw new Error('Unable to save follow notification preferences');
      setPreferences(data?.notification_preferences || nextPreferences);
    } catch (_error) {
      setPreferences(previousPreferences);
    } finally {
      setSavingCategory(null);
    }
  }

  return (
    <div ref={controlRef} className={`profile-follow-notifications${hasShare ? ' profile-follow-notifications--with-share' : ''}`}>
      <button
        type="button"
        className={`profile-follow-notifications__trigger${open ? ' profile-follow-notifications__trigger--open' : ''}`}
        aria-label={t('trigger')}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <ProfileNotificationsIcon aria-hidden="true" className="profile-follow-notifications__icon" />
      </button>

      {open && (
        <div className="profile-follow-notifications__menu" role="dialog" aria-label={t('title', { username: profile?.user_username || '' })}>
          <p className="profile-follow-notifications__title">{t('title', { username: profile?.user_username || '' })}</p>
          <div className="profile-follow-notifications__options">
            {categories.map((category) => (
              <div key={category.id} className="profile-follow-notifications__option">
                <ToggleSwitch
                  ariaLabel={t(`options.${category.titleKey}`)}
                  checked={visiblePreferences[category.id]}
                  disabled={Boolean(savingCategory) || (category.id === 'channel' && !channelNotificationsEnabled)}
                  onChange={(enabled) => updateCategory(category.id, enabled)}
                />
                <div className="profile-follow-notifications__copy">
                  <span>{t(`options.${category.titleKey}`)}</span>
                  <small>{category.id === 'channel' && !channelNotificationsEnabled
                    ? t('descriptions.channel_notifications_deactivated')
                    : t(`descriptions.${category.descriptionKey}`)}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
